import React, { useEffect, useMemo, useState } from 'react';
import { useQrGenerator } from '../../../utils/customHooks';
import { useFetchLqr } from '../../../utils/hooks/useFetchLqr';
import { useAuth } from '../../../utils/Auth';
import API from '../../../utils/API';
import styles from './QrCode.module.css';

export default function QrCode() {
    
    const { route, userObj } = useAuth();
    const isEnterprise = useMemo(() => userObj?.hasOwnProperty('enterprise'), [userObj?.enterprise]);
    const [ state, setState ] = useState({ 
        copied: false,
        fontColor: '#364957',
        imgSrc: null,
        loading: false,
        qrStyle: null,
        text: '',
        url: `https://bitsignal.io/${route}`
    });
    const qrStyle = useFetchLqr({qrId: userObj?.liquidqr?.qrId, lastUpdate: userObj?.liquidqr?.lastUpdate, route: route});
    const qrCode = useQrGenerator({qrStyle: state.qrStyle, route: route});
    const hasLqr = useMemo(() => (userObj?.liquidqr && userObj?.liquidqr?.qrId) ? true : false, [qrStyle, userObj?.liquidqr, userObj?.liquidqr?.qrId]);
    const qrImage = useMemo(() => {
        if (state.imgSrc?.startsWith('blob:')) {
            setState(prev => ({ ...prev, loading: false }));
            return (
                <div className={styles.qrCode}>
                    <img src={state.imgSrc} alt='custom QR' />
                </div>
            );
        };
        setState(prev => ({ ...prev, loading: false }));
        return null;
    },[state.imgSrc]);
    
    const loading = useMemo(() => {
        if (process.env.REACT_APP_LQR !== 'false') {
            if (!userObj || !state.imgSrc || state.loading) return true;
            if (userObj?.hasOwnProperty('liquidqr') && hasLqr && !qrStyle?.error && (!state.qrStyle || state.qrStyle?.data?.includes(route) || qrCode?._options?.data?.includes(route))) return true;
        };
        return false;
    },[userObj, userObj?.liquidqr, state.loading, state.qrStyle, state.imgSrc, qrStyle, qrCode, hasLqr]);

    const handleQrDownload = () => {
        qrCode.download({extension: 'png'});
    };

    const isMobileDevice = () => {
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isMobile = /mobile/.test(userAgent);
        return isMobile;
    };

    const copyToClipboard = async (text) => {
        if (navigator.share && isMobileDevice()) {
            try {
                await navigator.share({
                    title: 'Digital Business Cards by BitSignal',
                    text: `Check out my Digital Business Card! \n\n${text}`,
                });
            } catch (error) {
                setState(prev => ({ ...prev, copied: true, fontColor: 'red', text: 'Error' }));
                setTimeout(() => {
                    setState(prev => ({ ...prev, copied: false, text: '' }));
                }, 2000);
            };
        } else {
            try {
                await navigator.clipboard.writeText(text);
                setState(prev => ({ ...prev, copied: true, fontColor: 'green', text: 'Copied'}));
                setTimeout(() => {
                    setState(prev => ({ ...prev, copied: false, text: '' }));
                }, 2000);
            } catch (err) {
                console.error('Async: Could not copy text: ', err);
                setState(prev => ({ ...prev, copied: true, fontColor: 'red', text: 'Error' }));
                setTimeout(() => {
                    setState(prev => ({ ...prev, copied: false, text: '' }));
                }, 2000);
            };
        };
    };

    const handleConnectLiquidQr = () => !isEnterprise && window.open(`${process.env.REACT_APP_LQR_ORIGIN}/authenticate?bsUid=${userObj.uid}`);
    const handleEditQr = () => !isEnterprise && window.open(`${process.env.REACT_APP_LQR_ORIGIN}/collection/edit?qrid=${userObj.liquidqr.qrId}&edit=true&ref=bs&uid=${userObj.liquidqr.uid}`);

    const handleSaveAuth = async (lqrObj) => {
        if (!lqrObj?.uid) return;
        
        try {
            await API.connectLiquidQrAccount(lqrObj);
        } catch (err) {
            console.error(err);
        };
    };

    useEffect(() => {
        if (qrStyle && state.qrStyle === null) setState(prev => ({ ...prev, qrStyle: qrStyle }))
    },[qrStyle, state.qrStyle]);

    useEffect(() => {
        let isMounted = true;
        const handleGetImageBlob = async () => {
            try {
                setState(prev => ({ ...prev, loading: true }));
                const blob = await qrCode.getRawData('png');
                isMounted && setState(prev => ({ ...prev, imgSrc: URL.createObjectURL(blob)}));
            } catch (err) {
                console.error(err);
            };
        };

        if (qrCode) {
            handleGetImageBlob()
        };

        return () => { isMounted = false; };
    }, [qrCode]);

    useEffect(() => {
        const handleEventListener = async e => {
            if (e?.origin !== process.env.REACT_APP_LQR_ORIGIN) return;
            const lqrObj = e?.data;
            switch(lqrObj.type) {
                case 'edit':
                    if (!lqrObj.success) return;
                    await API.updateUserObject({ [`liquidqr/lastUpdated`]: Date.now() });
                    setState(prev => ({ ...prev, qrStyle: lqrObj.qr_style }));
                    break;
                case 'connect':
                    if (lqrObj?.uid) handleSaveAuth(lqrObj);
                    break;
                default:
                    break;
            };
        };

        window?.addEventListener('message', handleEventListener);
        return () => window.removeEventListener('message', handleEventListener);
    },[]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerInner}>
                    <div className={styles.title}>QR Code</div>
                    <div className={styles.subtitle}>Easily share your Digital Business Card</div>
                </div> 
                <div className={styles.downloadBtn} onClick={handleQrDownload}>
                    <i className='far fa-download' />
                    Download
                </div>
            </div>
            <div className={styles.qrContainer}>
                {(!loading && state.imgSrc !== null && qrCode !== null) ? qrImage : <div className={styles.qrLoading}><i className='far fa-spinner fa-spin' /></div>}
                <div className={styles.share}>
                    <div className={styles.shareHeader}>Your Sharable Link</div>
                    <div className={styles.shareInner}>
                        <span title={state.url}>{state.url}</span>
                        <button className={styles.shareBtn} onClick={() => copyToClipboard(state.url)} title='Copy to Clipboard'>
                            {state.copied ? <i className='fas fa-clipboard-check' /> : <i className='fas fa-clipboard' />}
                            {/* {state.text ? <span className={styles.shareMessage} style={{color: state.fontColor}}>{state.text}</span> : <></>} */}
                        </button>
                    </div>
                    <button className={styles.connectBtn} onClick={hasLqr ? handleEditQr : handleConnectLiquidQr} disabled={isEnterprise}>{hasLqr ? 'Edit your QR' : 'Connect to Liquid QR'}</button>
                </div>
            </div>
        </div>
    );
};
